import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Zap,
  Linkedin,
  Facebook,
  Twitter,
  Youtube,
  ArrowRight,
} from "lucide-react";
import { COMPANY } from "@/data/company";
import { PRODUCTS } from "@/data/products";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      setLoading(true);
      await subscribeNewsletter(email);
      toast.success("Subscribed! Watch your inbox for updates.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      className="bg-vortex-navy text-white/85 relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
      <div className="container-wide relative pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16">
          {/* Company */}
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-3 bg-white rounded-xl px-4 py-3 mb-5">
              <img
                src="https://customer-assets.emergentagent.com/job_vortex-power-preview/artifacts/7ao8fprf_vortex-logo.png"
                alt="Vortex Power Solutions"
                className="h-10 w-auto"
              />
              <div className="leading-tight border-l border-vortex-border pl-3">
                <div className="text-[10px] tracking-[0.25em] font-bold text-vortex-navy uppercase">
                  Power
                </div>
                <div className="text-[10px] tracking-[0.25em] font-bold text-vortex-slate uppercase">
                  Solutions
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm">
              {COMPANY.description}
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Linkedin, label: "LinkedIn" },
                { Icon: Facebook, label: "Facebook" },
                { Icon: Twitter, label: "Twitter" },
                { Icon: Youtube, label: "YouTube" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-vortex-blue hover:border-vortex-blue hover:text-vortex-navy transition-colors"
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h4 className="text-white font-heading font-bold text-sm uppercase tracking-widest mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/"
                  className="hover:text-vortex-blue transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="hover:text-vortex-blue transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="hover:text-vortex-blue transition-colors"
                >
                  Products
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-vortex-blue transition-colors"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div className="md:col-span-3">
            <h4 className="text-white font-heading font-bold text-sm uppercase tracking-widest mb-5">
              Products
            </h4>
            <ul className="space-y-3 text-sm">
              {PRODUCTS.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/products/${p.slug}`}
                    className="hover:text-vortex-blue transition-colors"
                    data-testid={`footer-product-${p.slug}`}
                  >
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div className="md:col-span-3">
            <h4 className="text-white font-heading font-bold text-sm uppercase tracking-widest mb-5">
              Get in Touch
            </h4>
            <ul className="space-y-3 text-sm mb-6">
              <li className="flex gap-3">
                <MapPin
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-vortex-blue"
                  strokeWidth={2}
                />
                <span className="text-white/70">
                  {COMPANY.address.line1}, {COMPANY.address.line2},{" "}
                  {COMPANY.address.city}, {COMPANY.address.state} –{" "}
                  {COMPANY.address.zip}
                </span>
              </li>
              <li className="flex gap-3">
                <Phone
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-vortex-blue"
                  strokeWidth={2}
                />
                <a
                  href={`tel:${COMPANY.phone}`}
                  className="hover:text-vortex-blue transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-vortex-blue"
                  strokeWidth={2}
                />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-vortex-blue transition-colors"
                >
                  {COMPANY.email}
                </a>
              </li>
            </ul>
            <form onSubmit={handleSubscribe} data-testid="newsletter-form">
              <label className="text-xs uppercase tracking-widest text-white/60 font-semibold">
                Newsletter
              </label>
              <div className="mt-2 flex bg-white/8 rounded-full border border-white/15 overflow-hidden focus-within:border-vortex-blue transition-colors">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-vortex-orange hover:bg-vortex-orange-deep transition-colors px-4 text-white disabled:opacity-70"
                  data-testid="newsletter-submit-btn"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
          <div>
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-5">
            <a href="#" className="hover:text-vortex-blue transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-vortex-blue transition-colors">
              Terms
            </a>
            <a href="#" className="hover:text-vortex-blue transition-colors">
              Sitemap
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
